import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  GetStores,
  GetActiveStores,
  CreateStore,
  UpdateStore,
  DeleteStore,
  UpdateStoreLocation } from "../Api";
import { 
  ServiceQueryOptions,
  unwrapApiResponse } from "./query_helpers";
import { DEFAULT_STALE_TIME, LOOKUP_STALE_TIME } from "../Constant/AppConstants";

export const storeQueryKeys = {
  stores: (params?: Parameters<typeof GetStores>[0]) =>
    ["stores", params] as const,
  activeStores: () => ["activeStores"] as const,
};

export const invalidateStoreQueries = (queryClient: any) => {
  queryClient.invalidateQueries({ queryKey: ["stores"] });
  queryClient.invalidateQueries({ queryKey: ["activeStores"] });
};

export const useStoresQuery = (
  params: Parameters<typeof GetStores>[0],
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: DEFAULT_STALE_TIME,
    ...options,
    queryKey: storeQueryKeys.stores(params),
    queryFn: async () => unwrapApiResponse(await GetStores(params)),
  });
};

export const useStoresRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: Parameters<typeof GetStores>[0]) =>
      queryClient.fetchQuery({
        staleTime: DEFAULT_STALE_TIME,
        queryKey: storeQueryKeys.stores(params),
        queryFn: async () => unwrapApiResponse(await GetStores(params)),
      }),
  });
};

export const useActiveStoresQuery = (
  tenantId?: string,
  options?: ServiceQueryOptions,
) => {
  return useQuery({
    staleTime: LOOKUP_STALE_TIME,
    ...options,
    queryKey: storeQueryKeys.activeStores(),
    queryFn: async () =>
      unwrapApiResponse(await GetActiveStores({ tenant_id: tenantId })),
  });
};

export const useActiveStoresRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      queryClient.fetchQuery({
        staleTime: LOOKUP_STALE_TIME,
        queryKey: storeQueryKeys.activeStores(),
        queryFn: async () => unwrapApiResponse(await GetActiveStores()),
      }),
  });
};

export const useCreateStoreMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof CreateStore>[0]) =>
      CreateStore(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateStoreQueries(queryClient);
      }
    },
  });
};

export const useUpdateStoreMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof UpdateStore>[0]) =>
      UpdateStore(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateStoreQueries(queryClient);
      }
    },
  });
};

export const useDeleteStoreMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof DeleteStore>[0]) =>
      DeleteStore(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateStoreQueries(queryClient);
      }
    },
  });
};

export const useStoreService = (config?: {
  fetchStoresParams?: Parameters<typeof GetStores>[0];
  fetchStoresEnabled?: boolean;
  fetchActiveStoresEnabled?: boolean;
}) => {
  const storesQuery = useStoresQuery(config?.fetchStoresParams, {
    enabled:
      config?.fetchStoresEnabled !== false &&
      config?.fetchStoresParams !== undefined &&
      config?.fetchStoresParams !== null,
  });

  const activeStoresQuery = useActiveStoresQuery(undefined, {
    enabled: config?.fetchActiveStoresEnabled !== false,
  });

  const createStoreMutation = useCreateStoreMutation();
  const updateStoreMutation = useUpdateStoreMutation();
  const deleteStoreMutation = useDeleteStoreMutation();
  const updateStoreLocationMutation = useUpdateStoreLocationMutation();

  return {
    storesQuery,
    activeStoresQuery,
    createStoreMutation,
    updateStoreMutation,
    deleteStoreMutation,
    updateStoreLocationMutation,
  };
};

export const useUpdateStoreLocationMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: Parameters<typeof UpdateStoreLocation>[0]) =>
      UpdateStoreLocation(payload),
    onSuccess: (res) => {
      if (res?.success) {
        invalidateStoreQueries(queryClient);
      }
    },
  });
};
